import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { UsePaginationReturn } from "@/hooks/usePagination";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

export interface TablePaginationProps {
  pagination: UsePaginationReturn;
  rowsPerPageLabel: string;
  pageOfLabel: string;
  onRefresh?: () => void | Promise<void>;
  isRefreshing?: boolean;
}

export function TablePagination({
  pagination,
  rowsPerPageLabel,
  pageOfLabel,
  onRefresh,
  isRefreshing = false,
}: TablePaginationProps): JSX.Element | null {
  const { t } = useTranslation();
  const {
    page,
    limit,
    total,
    pageNumbers,
    hasNextPage,
    hasPrevPage,
    goToPage,
    setLimit,
    pageSizeOptions,
  } = pagination;

  if (total === 0) {
    return null;
  }

  const handleRefresh = (): void => {
    if (onRefresh) void onRefresh();
  };

  return (
    <div className="flex flex-col gap-3 border-t border-border/80 bg-muted/50 px-3 py-3 sm:flex-row sm:flex-nowrap sm:items-center md:gap-4 md:px-4 md:py-4 dark:border-border/70 dark:bg-secondary/40">
      <div className="flex min-w-0 items-center gap-3 sm:flex-1 sm:gap-4">
        {onRefresh != null ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label={t("pages.common.pagination.refresh")}
            title={t("pages.common.pagination.refresh")}
          >
            <RefreshCw
              className={isRefreshing ? "h-4 w-4 animate-spin" : "h-4 w-4"}
              aria-hidden
            />
          </Button>
        ) : null}
        <div className="hidden items-center gap-2 sm:flex">
          <Label
            htmlFor="rows-per-page"
            className="whitespace-nowrap text-sm text-muted-foreground"
          >
            {rowsPerPageLabel}
          </Label>
          <Select
            value={String(limit)}
            onValueChange={(v) => setLimit(Number(v))}
          >
            <SelectTrigger id="rows-per-page" className="h-9 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-center gap-2 whitespace-nowrap text-xs text-muted-foreground sm:ml-0 sm:text-sm">
          {pageOfLabel}
        </div>
      </div>
      <div className="flex justify-center sm:flex-1 sm:shrink-0">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => hasPrevPage && goToPage(page - 1)}
                disabled={!hasPrevPage}
                aria-disabled={!hasPrevPage}
              />
            </PaginationItem>
            {pageNumbers.map((p, idx) =>
              p === "ellipsis" ? (
                <PaginationItem key={`ellipsis-${idx}`}>
                  <span className="flex h-9 w-9 items-center justify-center text-muted-foreground">
                    ...
                  </span>
                </PaginationItem>
              ) : (
                <PaginationItem key={p}>
                  <PaginationLink
                    isActive={page === p}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </PaginationLink>
                </PaginationItem>
              )
            )}
            <PaginationItem>
              <PaginationNext
                onClick={() => hasNextPage && goToPage(page + 1)}
                disabled={!hasNextPage}
                aria-disabled={!hasNextPage}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
      <div className="hidden min-w-0 sm:block sm:flex-1" aria-hidden />
    </div>
  );
}
